import { useState, useEffect } from 'react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import CrossfadeHero from '../../components/storefront/CrossfadeHero';
import ScrollProgress from '../../components/storefront/ScrollProgress';
import BenefitCards from '../../components/storefront/BenefitCards';
import PolarBear from '../../components/storefront/PolarBear';
import ResearchPapers from '../../components/storefront/ResearchPapers';
import { Icon } from '../../components/ui/Icon';
import { useCartCtx } from '../../App';
import { useCurrency } from '../../hooks/useCurrency';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import styles from './BlueCremeProduct.module.css';

const ACCENT = '#2f7fb5';

const BENEFITS = [
  {
    icon: 'dna',
    title: 'Boosts Collagen & Elastin',
    summary: 'Tap for the mechanism →',
    more: 'GHK-Cu signals dermal fibroblasts to ramp up synthesis of collagen and elastin — the scaffolding that keeps skin looking firm and springy. Reviews report increased collagen, elastin and glycosaminoglycan production.',
  },
  {
    icon: 'gem',
    title: 'Firms & Smooths Wrinkles',
    summary: 'Tap for the results →',
    more: 'In twice-daily topical studies summarized by Pickart, GHK-Cu creams improved the appearance of skin density, thickness, firmness and fine lines and laxity over ~12 weeks.',
  },
  {
    icon: 'shield',
    title: 'Antioxidant & Soothing',
    summary: 'Tap to learn more →',
    more: 'GHK helps quench damaging free radicals and calm the look of irritation, helping protect skin from oxidative and environmental stress that accelerates visible aging.',
  },
  {
    icon: 'droplet',
    title: 'Hydration & Barrier Support',
    summary: 'Tap for details →',
    more: "By stimulating glycosaminoglycans like dermatan and chondroitin sulfate, GHK-Cu supports the skin's water-holding matrix for a plumper, more cushioned, hydrated feel.",
  },
  {
    icon: 'bandage',
    title: 'Skin Remodeling & Renewal',
    summary: 'Tap for the science →',
    more: 'GHK-Cu is a long-studied skin-conditioning and tissue-remodeling signal — it helps support the look of healthy, renewed dermal structure.',
  },
  {
    icon: 'microscope',
    title: 'Resets Aging Gene Patterns',
    summary: 'Tap to expand →',
    more: 'Gene-expression data show GHK can shift the activity of hundreds of genes associated with aging back toward a younger, more regenerative profile.',
  },
];

const PAPERS = [
  {
    title:
      'Regenerative and Protective Actions of the GHK-Cu Peptide in the Light of the New Gene Data',
    authors: 'Pickart L, Margolina A.',
    journal: 'Int. J. Mol. Sci.',
    year: '2018; 19(7):1987',
    summary:
      "A comprehensive review tying GHK-Cu's skin actions to gene-level data: stimulating collagen, elastin and glycosaminoglycan synthesis, supporting fibroblast function, blood-vessel and nerve outgrowth, plus antioxidant and soothing protection.",
    url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6073405/',
  },
  {
    title:
      'GHK Peptide as a Natural Modulator of Multiple Cellular Pathways in Skin Regeneration',
    authors: 'Pickart L, Vasquez-Soltero JM, Margolina A.',
    journal: 'BioMed Research International.',
    year: '2015; 648108',
    summary:
      'Reviews how GHK supports the look of aged skin — tightening the appearance of loose skin, improving the look of elasticity and clarity, and reducing the appearance of fine lines, spots and photodamage — alongside its antioxidant role.',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC4508379/',
  },
  {
    title: 'Skin Regenerative and Anti-Cancer Actions of Copper Peptides',
    authors: 'Pickart L, Margolina A.',
    journal: 'Cosmetics.',
    year: '2018; 5(2):29',
    summary:
      'Focuses on copper-peptide use in cosmetics, summarizing the safety record of small copper peptides and their regenerative, skin-conditioning effects in topical formulations.',
    url: 'https://www.mdpi.com/2079-9284/5/2/29',
  },
];

export default function BlueCremeProduct() {
  const { fmt } = useCurrency();
  const { addItem } = useCartCtx();
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api
      .get('/products/blue-creme')
      .then((r) => {
        setProduct(r.data);
        setVariant(r.data.variants[0]);
      })
      .catch(() => toast.error('Could not load Blue Crème.'));
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAdd = () => {
    if (!variant) return;
    addItem(variant, product, qty);
    toast.success('Added to bag');
  };

  if (product === null) {
    return <div className={styles.loading}>Loading Blue Crème…</div>;
  }

  const onSale =
    variant && variant.comparePriceInCents > variant.priceInCents;
  const soldOut = variant && variant.inventory < 1;

  return (
    <div className={styles.page}>
      <ScrollProgress color="linear-gradient(90deg,#aee3ff,#5fa3e0)" />
      <Navbar />

      {/* ============ HERO ============ */}
      <CrossfadeHero
        src="/videos/cream-loop.mp4"
        overlay="rgba(10,30,60,.2)"
        className={styles.hero}
      >
        <div className={styles.heroVeil} />
        <div className={styles.heroInner}>
          <span className={styles.kicker}>
            <Icon name="snowflake" className="w-4 h-4" /> GHK-Cu Copper Peptide
          </span>
          <h1>
            Repair, Elevate.
            <br />
            <b>Blue Crème</b>
          </h1>
          <p>
            A cooling copper-peptide cream built around GHK-Cu — the tripeptide
            your skin makes less of with age. Rebuild the look of firmness,
            smooth the appearance of lines, and wake skin up cold.
          </p>
          <div className={styles.heroCta}>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => scrollTo('buy')}
            >
              Shop {variant ? fmt(variant.priceInCents) : ''}
            </button>
            <button
              type="button"
              className={styles.btnGhost}
              onClick={() => scrollTo('research')}
            >
              See the science
            </button>
          </div>
        </div>
        <div className={styles.scrollHint}>Scroll ↓</div>
      </CrossfadeHero>

      {/* ============ MOLECULE INTRO ============ */}
      <section className={styles.molecule}>
        <div className={styles.wrap}>
          <div className={styles.secHead}>
            <div className={styles.eyebrow}>The peptide</div>
            <h2>
              Meet <b>GHK-Cu</b>
            </h2>
            <p>
              GHK-Cu is a naturally occurring copper-binding tripeptide —
              glycyl-L-histidyl-L-lysine — first identified in human plasma by
              Dr. Loren Pickart in 1973. Plasma levels fall by more than half
              between age 20 and 60, and topically it acts as a signal that
              supports the look of remodeled, renewed skin.
            </p>
            <div className={styles.molBadge}>Gly–His–Lys · Cu²⁺</div>
          </div>
        </div>
      </section>

      {/* ============ BENEFITS ============ */}
      <section className={styles.benefits} id="benefits">
        <div className={styles.wrap}>
          <div className={styles.secHead}>
            <div className={styles.eyebrow}>Topical benefits</div>
            <h2>
              What GHK-Cu does <b>on the skin</b>
            </h2>
            <p>Tap a cube to read the mechanism. Citations are listed below.</p>
          </div>
          <BenefitCards items={BENEFITS} accent={ACCENT} />
        </div>
      </section>

      {/* ============ STATS ============ */}
      <section className={styles.stats}>
        <div className={styles.wrap}>
          <div className={styles.statgrid}>
            <div className={styles.stat}>
              <div className={styles.num}>1973</div>
              <div className={styles.lbl}>year GHK was discovered in human plasma</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.num}>−60%</div>
              <div className={styles.lbl}>drop in plasma GHK from age 20 to 60</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.num}>12&nbsp;wk</div>
              <div className={styles.lbl}>typical study window for firmer-looking skin*</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.num}>100s</div>
              <div className={styles.lbl}>of skin-repair genes shifted toward younger patterns</div>
            </div>
          </div>
          <p className={styles.statNote}>
            *Reported in twice-daily topical studies summarized in the reviews
            below. Individual results vary.
          </p>
        </div>
      </section>

      {/* ============ RESEARCH ============ */}
      <section className={styles.research} id="research">
        <div className={styles.wrap}>
          <div className={styles.secHead}>
            <div className={styles.eyebrow}>The evidence</div>
            <h2>
              Selected <b>research papers</b>
            </h2>
            <p>
              Peer-reviewed literature on GHK and GHK-Cu in skin. Tap to expand;
              links open the source.
            </p>
          </div>
          <ResearchPapers papers={PAPERS} />
          <p className={styles.disclaimer}>
            Blue Crème is a cosmetic skincare product. These references describe
            the peptide GHK-Cu in the scientific literature and are provided for
            education; they are not claims that this product treats, cures, or
            prevents any condition. Statements have not been evaluated by the
            FDA.
          </p>
        </div>
      </section>

      {/* ============ BUY ============ */}
      <section className={styles.buy} id="buy">
        <div className={styles.wrap}>
          <div className={styles.buyCard}>
            <div className={styles.eyebrow}>Hero Cream</div>
            <h3>{product.name}</h3>
            {product.description && (
              <div className={styles.sub}>{product.description}</div>
            )}

            {variant && (
              <>
                <div className={styles.price}>
                  {fmt(variant.priceInCents)}
                  {onSale && <small>{fmt(variant.comparePriceInCents)}</small>}
                </div>

                <div className={styles.stock}>
                  {variant.inventory > 0
                    ? `${variant.inventory} in stock`
                    : 'Out of stock'}
                </div>

                <div className={styles.qtyrow}>
                  <button
                    type="button"
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    disabled={qty <= 1}
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>
                  <span>{qty}</span>
                  <button
                    type="button"
                    onClick={() => setQty((q) => q + 1)}
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>

                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={handleAdd}
                  disabled={soldOut}
                >
                  {soldOut ? 'Out of Stock' : 'Add to Bag'}
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ============ INTERACTIVE POLAR BEAR ============ */}
      <PolarBear />

      <Footer />
    </div>
  );
}
